import CreditCardIcon from '@mui/icons-material/CreditCard';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardMedia,
    Checkbox,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid,
    Hidden,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Paper,
    Snackbar,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import { keyframes, useTheme } from '@mui/system';
import {
    deleteOrganizationMedia,
    getSignedUrl,
    updateOrganization,
    uploadFile,
    viewOrganization,
} from 'api';
import { extractPhoneDigits, formatPhoneDisplay } from '../../utils/phoneFormat';
import { UpdateOrganizationPayload } from 'api/Organization/updateOrganization';
import { AxiosError } from 'axios';
import {
    ButtonWithLoader,
    SharedUsersAvatarGroup,
    SnackBar,
    TopBarWithShareIconLayout,
    UploadLogo,
} from 'components';
import OrganizationSharingDrawer from 'components/Navigation/OrganizationSharingDrawer';
import { Form, Formik } from 'formik';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import DbImage from 'images/db_icon.png';
import SprintAnalysisLogo from 'images/SprintAnalysisLogo.png';
import TemLogo from 'images/TemLogo.svg';
import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import QRCode from 'react-qr-code';
import { useCookies } from 'react-cookie';
import { useHistory, useParams } from 'react-router-dom';
import ROUTES from 'Routes/routes';
import { Organization, SharedUser, useToggle } from 'utils';
import * as yup from 'yup';
import createCheckoutSession from '../../api/Auth/createCheckoutSession';
import { createIntegration, getAllIntegrations } from '../../api/integrations.api';
import { getStripeExpressAccount } from '../../api/organizations.api';
import {
    isSubscribedToOrgAccess,
    isSubscribedToSqlAccess,
    isSubscribedToVideoAnalysis,
} from '../../app.functions';
import { Integration, StripeAccount } from '../../app.types';
import { UploadBanner } from '../../components/Functional/UploadBanner';
import { UploadVideo } from '../../components/Functional/UploadVideo';
import { OrganizationsContext } from '../../contexts/OrganizationsContext';
import { useAppDispatch } from '../../redux/store';
import { shuffleOrganizationsLoadKey } from '../../redux/reducers/auth';
import {
    hiddenOrganizationUuidsSelector,
    primaryOrganizationUuidSelector,
    setHiddenOrganizations,
    setPrimaryOrganization,
} from '../../redux/reducers/user';
import { useSelector } from 'react-redux';
import { updateHiddenOrganizations, updatePrimaryOrganization } from '../../api/Auth';
import { ReactComponent as OfficeDeskIcon } from '../../images/office-desk.svg';
import { ReactComponent as StripeIcon } from '../../images/stripe.svg';
import MarketplaceSettingsPage from './MarketplaceSettingsPage';
import PodsPage from './PodsPage';
import SqlUsersList from './SqlUsersList';
import InvoicesPage from './InvoicesPage';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { LocalOffer, SpeakerPhone, ArrowForward } from '@mui/icons-material';
import AttributesPage from './AttributesPage';
import FiyrPodLogo from '../../logos/FiyrPodLogoWBG.png';

const shimmerAnimation = keyframes`
    0% {
        background-position: 0% 0%;
    }
    100% {
        background-position: 200% 200%;
    }
`;

export default function EditOrganization(): ReactElement {
    const { subscriptions } = useContext(OrganizationsContext);
    const dispatch = useAppDispatch();
    const primaryOrganizationUuid = useSelector(primaryOrganizationUuidSelector);
    const hiddenOrganizationUuids = useSelector(hiddenOrganizationUuidsSelector);
    const [organization, setOrganization] = useState<Organization | null>(null);
    const [creating, setCreating] = useState(false);
    let { id } = useParams<{ id: string }>();
    const { push, goBack } = useHistory();
    const [uploadLogo, setUploadLogo] = useState<File>();
    const [uploadBanner, setUploadBanner] = useState<File>();
    const [uploadVideo, setUploadVideo] = useState<File>();
    const [tabValue, setTabValue] = useState(0);
    const [sharedUsers, setSharedUsers] = useState<Array<SharedUser>>([]);
    const [saving, setSaving] = useState(false);
    const [logoUploading] = useState(false);
    const [bannerUploading] = useState(false);
    const [videoUploading] = useState(false);
    const [title, setTitle] = useState('');
    const [slug, setSlug] = useState('');
    const [marketplaceEnabled, setMarketplaceEnabled] = useState<boolean>(false);
    const [originalSlug, setOriginalSlug] = useState(''); // Track original slug
    const [showSlugConfirmModal, setShowSlugConfirmModal] = useState(false); // Modal state
    const [aboutUs, setAboutUs] = useState('');
    const [zip, setZip] = useState('');
    const [ownerFirstName, setOwnerFirstName] = useState('');
    const [ownerLastName, setOwnerLastName] = useState('');
    const [ownerEmail, setOwnerEmail] = useState('');
    const [ownerPhone, setOwnerPhone] = useState('');
    const [originalOwnerFirstName, setOriginalOwnerFirstName] = useState('');
    const [originalOwnerLastName, setOriginalOwnerLastName] = useState('');
    const [originalOwnerEmail, setOriginalOwnerEmail] = useState('');
    const [originalOwnerPhone, setOriginalOwnerPhone] = useState('');
    const [notifyUserMessage, setNotifyUserMessage] = useState('');
    const [success, setSuccess] = useState(false);
    const [sharingOpen, toggleSharing] = useToggle();
    const [cookies, setCookie] = useCookies(['favOrg']);
    // Initialize from Redux, fallback to cookie for backward compatibility
    const [favOrg, setFavOrg] = useState(primaryOrganizationUuid || cookies.favOrg || null);
    const [temModal, setTemModal] = useState(false);
    const [sprintAnalysisModal, setSprintAnalysisModal] = useState(false);
    const [integrations, setIntegrations] = useState<Integration[]>([]);
    const [checkingOut, setCheckingOut] = useState(false);
    const theme = useTheme();
    const [stripeAccount, setStripeAccount] = React.useState<StripeAccount>();
    const [hasIntegratedStripe, setHasIntegratedStripe] = React.useState<boolean>(false);
    const [integrationsLoaded, setIntegrationsLoaded] = React.useState(false);
    const [snackMessage, setSnackMessage] = React.useState<string | null>(null);
    const [hasStripe, setHasStripe] = React.useState(false);
    const [muxPlaybackId, setMuxPlaybackId] = useState<string | undefined>(undefined);
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(1000));
    const [copySuccess, setCopySuccess] = useState(false);
    const [copySuccessAthlete, setCopySuccessAthlete] = useState(false);
    const [openQR, setOpenQR] = useState(false);
    const [openQRAthlete, setOpenQRAthlete] = useState(false);
    const [integrationsMenuAnchor, setIntegrationsMenuAnchor] = useState<null | HTMLElement>(null);
    const fiyrpodStoreUrl = `https://${slug}.fiyrpod.com`;
    const athleteOnboardingUrl = `https://joinfiyrpod.com/${slug}`;

    // Tab mapping: visual index -> internal tab value
    // Main tabs: 0=Org Details, 1=Back Office, 2=Invoices, 3=Pods, 4=3rd Party Integrations (dropdown)
    const TAB_MAPPING: { [key: number]: number } = {
        0: 0, // Organization Details
        1: 6, // Back Office
        2: 7, // Invoices
        3: 5, // Pods
        4: 1, // 3rd Party Integrations (defaults to Third Party Apps)
    };

    // Reverse mapping: internal tab value -> visual index
    const REVERSE_TAB_MAPPING: { [key: number]: number } = {
        0: 0, // Organization Details
        6: 1, // Back Office
        7: 2, // Invoices
        5: 3, // Pods
        1: 4, // Third Party Apps (part of 3rd Party Integrations)
        2: 4, // Premium Features (part of 3rd Party Integrations)
        3: 4, // SQL Access (part of 3rd Party Integrations)
        4: 4, // Setup Stripe (part of 3rd Party Integrations)
        8: 4, // Attributes (part of 3rd Party Integrations)
    };

    const getVisualTabIndex = (internalTabValue: number): number => {
        return REVERSE_TAB_MAPPING[internalTabValue] ?? 0;
    };

    const getInternalTabValue = (visualIndex: number): number => {
        return TAB_MAPPING[visualIndex] ?? 0;
    };

    const handleClickOpenQR = () => {
        setOpenQR(true);
    };

    const handleCloseQR = () => {
        setOpenQR(false);
    };

    const handleClickOpenQRAthlete = () => {
        setOpenQRAthlete(true);
    };

    const handleCloseQRAthlete = () => {
        setOpenQRAthlete(false);
    };

    const copyToClipboard = useCallback(() => {
        const urlToCopy = fiyrpodStoreUrl;

        const fallbackCopyTextToClipboard = (text: string) => {
            const textArea = document.createElement('textarea');
            textArea.value = text;

            // Avoid scrolling to bottom
            textArea.style.top = '0';
            textArea.style.left = '0';
            textArea.style.position = 'fixed';

            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            try {
                const successful = document.execCommand('copy');
                if (successful) {
                    setCopySuccess(true);
                    setTimeout(() => setCopySuccess(false), 2000);
                } else {
                    console.error('Fallback: Unable to copy');
                }
            } catch (err) {
                console.error('Fallback: Unable to copy', err);
            }

            document.body.removeChild(textArea);
        };

        if (!navigator.clipboard) {
            fallbackCopyTextToClipboard(urlToCopy);
            return;
        }
        navigator.clipboard.writeText(urlToCopy).then(
            () => {
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
            },
            (err) => {
                console.error('Async: Could not copy text: ', err);
                fallbackCopyTextToClipboard(urlToCopy);
            },
        );
    }, [fiyrpodStoreUrl]);

    const copyAthleteUrlToClipboard = useCallback(() => {
        const urlToCopy = athleteOnboardingUrl;

        const fallbackCopyTextToClipboard = (text: string) => {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.top = '0';
            textArea.style.left = '0';
            textArea.style.position = 'fixed';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                const successful = document.execCommand('copy');
                if (successful) {
                    setCopySuccessAthlete(true);
                    setTimeout(() => setCopySuccessAthlete(false), 2000);
                }
            } catch (err) {
                console.error('Fallback: Unable to copy', err);
            }
            document.body.removeChild(textArea);
        };

        if (!navigator.clipboard) {
            fallbackCopyTextToClipboard(urlToCopy);
            return;
        }
        navigator.clipboard.writeText(urlToCopy).then(
            () => {
                setCopySuccessAthlete(true);
                setTimeout(() => setCopySuccessAthlete(false), 2000);
            },
            (err) => {
                console.error('Async: Could not copy text: ', err);
                fallbackCopyTextToClipboard(urlToCopy);
            },
        );
    }, [athleteOnboardingUrl]);

    // Sync cookie to Redux on mount for backward compatibility
    useEffect(() => {
        if (!primaryOrganizationUuid && cookies.favOrg) {
            // Extract UUID from cookie format: "OrganizationNameUUID"
            // The UUID is typically at the end, so we need to find it
            // For now, we'll store the full identifier and let other parts extract the UUID
            // But ideally, we should extract just the UUID
            // Try to extract UUID - assuming format is consistent
            // If cookie contains organization data, we might need to look it up
            // For now, we'll keep the cookie sync but prioritize Redux
        }
    }, [primaryOrganizationUuid, cookies.favOrg]);

    useEffect(() => {
        const fetchStripeAccountStatus = async () => {
            if (stripeAccount && hasIntegratedStripe && integrationsLoaded) {
                setHasStripe(true);
            }
        };

        void fetchStripeAccountStatus();
    });

    useEffect(() => {
        const fetchIntegrations = async () => {
            if (organization) {
                // Ensure `organization` is not null before accessing its properties
                try {
                    const response = await getAllIntegrations(organization.uuid);
                    setIntegrations(response.data);
                    setIntegrationsLoaded(true);
                } catch (e) {
                    setSnackMessage(
                        'Error while loading required information to display this page',
                    );
                }
            }
        };

        void fetchIntegrations();
    }, [organization]);

    const handleTabValueChange = (event: React.SyntheticEvent, visualIndex: number) => {
        // If clicking on 3rd Party Integrations tab (index 4), open the dropdown menu
        if (visualIndex === 4) {
            setIntegrationsMenuAnchor(event.currentTarget as HTMLElement);
        } else {
            const internalValue = getInternalTabValue(visualIndex);
            setTabValue(internalValue);
        }
    };

    const handleIntegrationsMenuClose = (internalTabValue?: number) => {
        setIntegrationsMenuAnchor(null);
        if (internalTabValue !== undefined) {
            setTabValue(internalTabValue);
        }
    };

    // Handle primary organization selection (just updates UI, doesn't save yet)
    const handlePrimaryOrgChange = useCallback(
        async (event: React.SyntheticEvent, checked: boolean) => {
            if (!organization?.uuid) return;

            if (checked) {
                const orgIdentifier = `${organization.name}${organization.uuid}`;
                setFavOrg(orgIdentifier);
                // Save to Redux for global state
                dispatch(setPrimaryOrganization(organization.uuid));

                // If organization is hidden, automatically un-hide it (can't hide primary)
                if (hiddenOrganizationUuids?.includes(organization.uuid)) {
                    const currentHidden = Array.isArray(hiddenOrganizationUuids)
                        ? hiddenOrganizationUuids
                        : [];
                    const newHidden = currentHidden.filter((uuid) => uuid !== organization.uuid);
                    dispatch(setHiddenOrganizations(newHidden));
                    // Save to backend
                    try {
                        await updateHiddenOrganizations(newHidden);
                    } catch (error) {
                        console.error('Failed to un-hide organization:', error);
                    }
                }

                // Save to backend
                try {
                    await updatePrimaryOrganization(organization.uuid);
                    setSnackMessage(
                        hiddenOrganizationUuids?.includes(organization.uuid)
                            ? 'Primary organization saved and automatically made visible!'
                            : 'Primary organization saved successfully!',
                    );
                    setSuccess(true);
                } catch (error) {
                    console.error('Failed to save primary organization to backend:', error);
                    setSnackMessage('Failed to save primary organization');
                    setSuccess(false);
                    // Revert Redux on error
                    dispatch(setPrimaryOrganization(null));
                    setFavOrg(null);
                }
                // Also save to cookie for backward compatibility
                setCookie(`favOrg`, orgIdentifier, {
                    domain: '.1tul.com',
                    path: '/',
                });
            } else {
                // Unset primary organization
                dispatch(setPrimaryOrganization(null));
                setFavOrg(null);
                // Save to backend
                try {
                    await updatePrimaryOrganization(null);
                    setSnackMessage('Primary organization removed successfully!');
                    setSuccess(true);
                } catch (error) {
                    console.error('Failed to save primary organization to backend:', error);
                    setSnackMessage('Failed to save primary organization');
                    setSuccess(false);
                    // Revert Redux on error
                    dispatch(setPrimaryOrganization(organization.uuid));
                    setFavOrg(`${organization.name}${organization.uuid}`);
                }
                setCookie(`favOrg`, '', {
                    domain: '.1tul.com',
                    path: '/',
                });
            }
        },
        [organization?.name, organization?.uuid, hiddenOrganizationUuids, dispatch, setCookie],
    );

    const handleHideOrganizationChange = useCallback(
        async (event: React.SyntheticEvent, checked: boolean) => {
            if (!organization?.uuid) return;

            // Ensure hiddenOrganizationUuids is always an array
            const currentHidden = Array.isArray(hiddenOrganizationUuids)
                ? hiddenOrganizationUuids
                : [];
            let newHidden: string[];

            if (checked) {
                // Add to hidden list
                newHidden = [...currentHidden, organization.uuid];
                // If this was the primary organization, automatically unset it as primary
                if (organization.uuid === primaryOrganizationUuid) {
                    dispatch(setPrimaryOrganization(null));
                    setFavOrg(null);
                    // Save to backend
                    try {
                        await updatePrimaryOrganization(null);
                        setSnackMessage(
                            `${organization.name} hidden from filters and removed as primary organization.`,
                        );
                        setSuccess(true);
                    } catch (error) {
                        console.error('Failed to unset primary organization:', error);
                        setSnackMessage(
                            'Failed to unset primary organization, but organization was hidden.',
                        );
                        setSuccess(false);
                    }
                }
            } else {
                // Remove from hidden list
                newHidden = currentHidden.filter((uuid) => uuid !== organization.uuid);
            }

            // Update Redux
            dispatch(setHiddenOrganizations(newHidden));

            // Save to backend
            try {
                console.log('Saving hidden organizations:', {
                    organizationUuid: organization?.uuid,
                    newHidden,
                    currentHidden,
                });
                await updateHiddenOrganizations(newHidden);
                // Only show message if we didn't already show one for unsetting primary
                if (!(checked && organization.uuid === primaryOrganizationUuid)) {
                    setSnackMessage(
                        checked
                            ? `${organization.name} hidden from filters`
                            : `${organization.name} shown in filters`,
                    );
                    setSuccess(true);
                }
            } catch (error: any) {
                console.error('Failed to save hidden organizations:', error);
                const errorMessage =
                    error?.response?.data?.message ||
                    error?.response?.data?.errors?.hidden_organization_uuids?.[0] ||
                    'Failed to update hidden organizations';
                setSnackMessage(errorMessage);
                setSuccess(false);
                // Revert Redux on error
                dispatch(setHiddenOrganizations(currentHidden));
            }
        },
        [
            organization?.uuid,
            organization?.name,
            hiddenOrganizationUuids,
            dispatch,
            primaryOrganizationUuid,
        ],
    );

    const deleteVideo = async () => {
        try {
            await deleteOrganizationMedia(organization?.uuid, 'video');
        } catch (error) {
            console.error('Failed to delete video:', error);
        }
    };

    const deleteBanner = async () => {
        try {
            await deleteOrganizationMedia(organization?.uuid, 'banner');
        } catch (error) {
            console.error('Failed to delete banner:', error);
        }
    };

    const deleteLogo = async () => {
        try {
            await deleteOrganizationMedia(organization?.uuid, 'logo');
        } catch (error) {
            console.error('Failed to delete logo:', error);
        }
    };

    const handleSubmit = async (payload: UpdateOrganizationPayload) => {
        console.debug('handle submit');
        setSaving(true);

        const response = await updateOrganization(id, payload);
        setOrganization(response.data);
        if (response.data.mux_playback_id) {
            // Wait before setting the playbackId to avoid Mux race condition.
            setTimeout(() => {
                setMuxPlaybackId(response.data.mux_playback_id);
            }, 5000);
        }
        setSaving(false);
        return response.data; // Return the updated organization data
    };

    const performSave = async () => {
        setSaving(true);

        interface Payload {
            name: string;
            slug: string;
            zip: string;
            about_us: string;
            logo?: string;
            banner?: string;
            video?: string;
            marketplace_enabled?: boolean;
            owner_first_name?: string | null;
            owner_last_name?: string | null;
            owner_email?: string | null;
            owner_phone?: string | null;
        }

        // Format phone number to E.164 format (+1 followed by exactly 10 digits)
        let formattedOwnerPhone = ownerPhone || null;
        if (formattedOwnerPhone) {
            // ownerPhone should already be just digits (10 or less), but clean it just in case
            const cleaned = formattedOwnerPhone
                .replace(/[\u200B-\u200D\uFEFF]/g, '') // Remove zero-width spaces
                .replace(/[\u202A-\u202E]/g, '') // Remove directional formatting
                .trim();

            // Extract only digits
            let phoneDigits = extractPhoneDigits(cleaned);

            // Normalize: if we have 11 digits starting with 1, remove the leading 1
            if (phoneDigits.length === 11 && phoneDigits.startsWith('1')) {
                phoneDigits = phoneDigits.substring(1);
            }
            // Limit to 10 digits maximum
            phoneDigits = phoneDigits.substring(0, 10);

            if (phoneDigits.length === 10) {
                // Exactly 10 digits: format as +1XXXXXXXXXX
                formattedOwnerPhone = `+1${phoneDigits}`;
            } else if (phoneDigits.length > 0 && phoneDigits.length < 10) {
                // Less than 10 digits: invalid, but try to send it and let backend validate
                formattedOwnerPhone = `+1${phoneDigits}`;
            } else {
                // Empty or invalid
                formattedOwnerPhone = null;
            }
        }

        let payload: Payload = {
            name: title,
            slug: slug,
            zip: zip,
            about_us: aboutUs,
            marketplace_enabled: marketplaceEnabled,
            owner_first_name: ownerFirstName || null,
            owner_last_name: ownerLastName || null,
            owner_email: ownerEmail || null,
            owner_phone: formattedOwnerPhone,
        };

        if (uploadLogo) {
            console.debug('upload logo');
            try {
                const response = await getSignedUrl();
                if (response.status === 201) {
                    const res = await uploadFile(response.data.url, uploadLogo);
                    if (res.status === 200) {
                        payload.logo = response.data.key;
                    }
                } else {
                    setSnackMessage('There was an error uploading the logo. Please try again.');
                    setSuccess(false);
                    return;
                }
            } catch (err: any) {
                const error: AxiosError<{ message: string }> = err;
                setSnackMessage(
                    `There was an error uploading the logo: ${
                        error.response?.data?.message ?? 'an unknown error occurred'
                    }`,
                );
                setSuccess(false);
                setSaving(false);
                return false;
            }
        }

        if (uploadBanner) {
            console.debug('upload banner');
            try {
                const response = await getSignedUrl();
                if (response.status === 201) {
                    const res = await uploadFile(response.data.url, uploadBanner);
                    if (res.status === 200) {
                        payload.banner = response.data.key;
                    }
                } else {
                    setSnackMessage('There was an error uploading the banner. Please try again.');
                    setSuccess(false);
                    return;
                }
            } catch (err: any) {
                const error: AxiosError<{ message: string }> = err;
                setSnackMessage(
                    `There was an error uploading the banner: ${
                        error.response?.data?.message ?? 'an unknown error occurred'
                    }`,
                );
                setSuccess(false);
                setSaving(false);
                return false;
            }
        }

        if (uploadVideo) {
            console.debug('upload video');
            try {
                const response = await getSignedUrl();
                if (response.status === 201) {
                    const res = await uploadFile(response.data.url, uploadVideo);
                    if (res.status === 200) {
                        payload.video = response.data.key;
                    }
                } else {
                    setSnackMessage('There was an error uploading the video. Please try again.');
                    setSuccess(false);
                    return;
                }
            } catch (err: any) {
                const error: AxiosError<{ message: string }> = err;
                setSnackMessage(
                    `There was an error uploading the video: ${
                        error.response?.data?.message ?? 'an unknown error occurred'
                    }`,
                );
                setSuccess(false);
                setSaving(false);
                return;
            }
        }

        try {
            console.debug('performSave() payload', payload);
            const updatedOrganization = await handleSubmit(payload);

            setSnackMessage('Organization updated successfully');
            setSuccess(true);
            // Update the original slug after successful save
            setOriginalSlug(slug);

            // Update owner fields from the updated organization response (fallback to creator if not set)
            if (updatedOrganization) {
                const newFirstName =
                    updatedOrganization.owner_first_name ??
                    updatedOrganization.creator?.first_name ??
                    '';
                const newLastName =
                    updatedOrganization.owner_last_name ??
                    updatedOrganization.creator?.last_name ??
                    '';
                const newEmail =
                    updatedOrganization.owner_email ?? updatedOrganization.creator?.email ?? '';
                const newPhoneRaw =
                    updatedOrganization.owner_phone ?? updatedOrganization.creator?.phone ?? '';
                // Extract digits only from the phone number (in case it's formatted as +15551234567)
                let newPhone = extractPhoneDigits(newPhoneRaw);
                // If we have 11 digits starting with 1, remove the leading 1 (US country code)
                if (newPhone.length === 11 && newPhone.startsWith('1')) {
                    newPhone = newPhone.substring(1);
                }
                // Limit to 10 digits maximum
                newPhone = newPhone.substring(0, 10);

                setOwnerFirstName(newFirstName);
                setOwnerLastName(newLastName);
                setOwnerEmail(newEmail);
                setOwnerPhone(newPhone);

                // Update original values after successful save
                setOriginalOwnerFirstName(newFirstName);
                setOriginalOwnerLastName(newLastName);
                setOriginalOwnerEmail(newEmail);
                setOriginalOwnerPhone(newPhone);
            } else {
                // Fallback: refresh organization data if response doesn't include updated data
                viewOrganization(id)
                    .then((response) => {
                        setOrganization(response.data);
                        // Update owner fields from organization (fallback to creator if not set)
                        const newFirstName =
                            response.data.owner_first_name ??
                            response.data.creator?.first_name ??
                            '';
                        const newLastName =
                            response.data.owner_last_name ?? response.data.creator?.last_name ?? '';
                        const newEmail =
                            response.data.owner_email ?? response.data.creator?.email ?? '';
                        const newPhoneRaw =
                            response.data.owner_phone ?? response.data.creator?.phone ?? '';
                        // Extract digits only from the phone number (in case it's formatted as +15551234567)
                        let newPhone = extractPhoneDigits(newPhoneRaw);
                        // If we have 11 digits starting with 1, remove the leading 1 (US country code)
                        if (newPhone.length === 11 && newPhone.startsWith('1')) {
                            newPhone = newPhone.substring(1);
                        }
                        // Limit to 10 digits maximum
                        newPhone = newPhone.substring(0, 10);

                        setOwnerFirstName(newFirstName);
                        setOwnerLastName(newLastName);
                        setOwnerEmail(newEmail);
                        setOwnerPhone(newPhone);

                        // Update original values after successful save
                        setOriginalOwnerFirstName(newFirstName);
                        setOriginalOwnerLastName(newLastName);
                        setOriginalOwnerEmail(newEmail);
                        setOriginalOwnerPhone(newPhone);
                    })
                    .catch(() => {});
            }

            // Refresh organizations context to update navigation bar logo
            dispatch(shuffleOrganizationsLoadKey());
        } catch (err: any) {
            const error: AxiosError<{ message: string }> = err;
            setSnackMessage(
                `There was an error updating the organization: ${
                    error.response?.data?.message ?? 'an unknown error occurred'
                }`,
            );
            setSuccess(false);
            setSaving(false);
        }
    };

    const handleSave = async () => {
        // Check if slug has changed
        if (slug !== originalSlug) {
            setShowSlugConfirmModal(true);
        } else {
            await performSave();
        }
    };

    const handleSlugConfirmation = async (confirmed: boolean) => {
        setShowSlugConfirmModal(false);

        if (confirmed) {
            await performSave();
        } else {
            // Reset slug to original value if user cancels
            setSlug(originalSlug);
        }
    };

    // Check if owner fields have changed
    const hasOwnerFieldsChanged = useMemo(() => {
        return (
            ownerFirstName !== originalOwnerFirstName ||
            ownerLastName !== originalOwnerLastName ||
            ownerEmail !== originalOwnerEmail ||
            ownerPhone !== originalOwnerPhone
        );
    }, [
        ownerFirstName,
        ownerLastName,
        ownerEmail,
        ownerPhone,
        originalOwnerFirstName,
        originalOwnerLastName,
        originalOwnerEmail,
        originalOwnerPhone,
    ]);

    // Save only owner fields
    const handleSaveOwnerFields = async () => {
        setSaving(true);

        interface Payload {
            owner_first_name?: string | null;
            owner_last_name?: string | null;
            owner_email?: string | null;
            owner_phone?: string | null;
        }

        // Format phone number to E.164 format (+1 followed by exactly 10 digits)
        let formattedOwnerPhone = ownerPhone || null;
        if (formattedOwnerPhone) {
            // ownerPhone should already be just digits (10 or less), but clean it just in case
            const cleaned = formattedOwnerPhone
                .replace(/[\u200B-\u200D\uFEFF]/g, '') // Remove zero-width spaces
                .replace(/[\u202A-\u202E]/g, '') // Remove directional formatting
                .trim();

            // Extract only digits
            let phoneDigits = extractPhoneDigits(cleaned);

            // Normalize: if we have 11 digits starting with 1, remove the leading 1
            if (phoneDigits.length === 11 && phoneDigits.startsWith('1')) {
                phoneDigits = phoneDigits.substring(1);
            }
            // Limit to 10 digits maximum
            phoneDigits = phoneDigits.substring(0, 10);

            if (phoneDigits.length === 10) {
                // Exactly 10 digits: format as +1XXXXXXXXXX
                formattedOwnerPhone = `+1${phoneDigits}`;
            } else if (phoneDigits.length > 0 && phoneDigits.length < 10) {
                // Less than 10 digits: invalid, but try to send it and let backend validate
                formattedOwnerPhone = `+1${phoneDigits}`;
            } else {
                // Empty or invalid
                formattedOwnerPhone = null;
            }
        }

        const payload: Payload = {
            owner_first_name: ownerFirstName || null,
            owner_last_name: ownerLastName || null,
            owner_email: ownerEmail || null,
            owner_phone: formattedOwnerPhone,
        };

        try {
            const updatedOrganization = await handleSubmit(payload);

            setSnackMessage('Owner information updated successfully');
            setSuccess(true);

            // Update owner fields from the updated organization response (fallback to creator if not set)
            if (updatedOrganization) {
                const newFirstName =
                    updatedOrganization.owner_first_name ??
                    updatedOrganization.creator?.first_name ??
                    '';
                const newLastName =
                    updatedOrganization.owner_last_name ??
                    updatedOrganization.creator?.last_name ??
                    '';
                const newEmail =
                    updatedOrganization.owner_email ?? updatedOrganization.creator?.email ?? '';
                const newPhoneRaw =
                    updatedOrganization.owner_phone ?? updatedOrganization.creator?.phone ?? '';
                // Extract digits only from the phone number (in case it's formatted as +15551234567)
                let newPhone = extractPhoneDigits(newPhoneRaw);
                // If we have 11 digits starting with 1, remove the leading 1 (US country code)
                if (newPhone.length === 11 && newPhone.startsWith('1')) {
                    newPhone = newPhone.substring(1);
                }
                // Limit to 10 digits maximum
                newPhone = newPhone.substring(0, 10);

                setOwnerFirstName(newFirstName);
                setOwnerLastName(newLastName);
                setOwnerEmail(newEmail);
                setOwnerPhone(newPhone);

                // Update original values after successful save
                setOriginalOwnerFirstName(newFirstName);
                setOriginalOwnerLastName(newLastName);
                setOriginalOwnerEmail(newEmail);
                setOriginalOwnerPhone(newPhone);
            }
        } catch (err: any) {
            const error: AxiosError<{ message: string }> = err;
            setSnackMessage(
                `There was an error updating owner information: ${
                    error.response?.data?.message ?? 'an unknown error occurred'
                }`,
            );
            setSuccess(false);
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (organization?.uuid) {
            getAllIntegrations(organization?.uuid)
                .then((response) => {
                    setIntegrations(response.data);
                })
                .catch(() => {})
                .finally(() => {});
        }
    }, [organization]);

    const SaveButton = () => (
        <Grid item sx={{ width: '100%' }}>
            <ButtonWithLoader
                onClick={() => handleSave()}
                loading={saving}
                disabled={saving}
                variant="contained"
                color="primary"
                sx={{
                    width: '100%',
                    height: '50%',
                    marginTop: theme.spacing(6),
                    marginBottom: theme.spacing(24),
                }}
            >
                Save
            </ButtonWithLoader>
        </Grid>
    );
    useEffect(() => {
        viewOrganization(id)
            .then((response) => {
                setOrganization(response.data);
                if (response.data.mux_playback_id) {
                    setMuxPlaybackId(response.data.mux_playback_id);
                }
                setTitle(response.data.name);
                setSlug(response.data.slug);
                setOriginalSlug(response.data.slug); // Set original slug
                setAboutUs(response.data.about_us ?? '');
                setZip(response.data.zip ?? '');
                setMarketplaceEnabled(response.data.marketplace_enabled);
                // Initialize owner information from organization owner fields (fallback to creator if not set)
                const initialFirstName =
                    response.data.owner_first_name ?? response.data.creator?.first_name ?? '';
                const initialLastName =
                    response.data.owner_last_name ?? response.data.creator?.last_name ?? '';
                const initialEmail =
                    response.data.owner_email ?? response.data.creator?.email ?? '';
                const initialPhoneRaw =
                    response.data.owner_phone ?? response.data.creator?.phone ?? '';
                // Extract digits only from the phone number (in case it's formatted as +15551234567)
                let initialPhone = extractPhoneDigits(initialPhoneRaw);
                // If we have 11 digits starting with 1, remove the leading 1 (US country code)
                if (initialPhone.length === 11 && initialPhone.startsWith('1')) {
                    initialPhone = initialPhone.substring(1);
                }
                // Limit to 10 digits maximum
                initialPhone = initialPhone.substring(0, 10);

                setOwnerFirstName(initialFirstName);
                setOwnerLastName(initialLastName);
                setOwnerEmail(initialEmail);
                setOwnerPhone(initialPhone);

                // Store original values for change detection
                setOriginalOwnerFirstName(initialFirstName);
                setOriginalOwnerLastName(initialLastName);
                setOriginalOwnerEmail(initialEmail);
                setOriginalOwnerPhone(initialPhone);
            })
            .catch(() => {
                push(ROUTES.NotFound.path);
            })
            .finally();
    }, [id, push]);

    useEffect(() => {
        setHasIntegratedStripe(
            integrations.filter((i) => i.api_type === 'stripe_express').length > 0,
        );
    }, [integrations]);

    useEffect(() => {
        const fetchStripeAccountStatus = async () => {
            if (organization) {
                try {
                    const response = await getStripeExpressAccount(organization.uuid);
                    setStripeAccount(response.data);
                } catch (e) {
                    setSnackMessage(
                        'Error while loading required information to display this page',
                    );
                }
            }
        };

        // if we have a stripe account, we need to fetch the status
        if (hasIntegratedStripe) {
            void fetchStripeAccountStatus();
        }
    }, [hasIntegratedStripe, organization]);

    useEffect(() => {
        if (tabValue === 6 && organization) {
            push(`/networking/${organization.uuid}`);
        }
    }, [push, tabValue, organization]);

    return (
        <TopBarWithShareIconLayout
            onBack={() => goBack()}
            onShare={() => toggleSharing()}
            title={`${organization?.name}  - Organization Settings` || ''}
            right={
                <div style={{ display: 'flex' }}>
                    <SharedUsersAvatarGroup sharedUsers={sharedUsers} />
                </div>
            }
        >
            <Grid container direction={isSmallScreen ? 'column' : 'row'} justifyContent="center">
                <Tabs
                    sx={{ my: 8, flexGrow: 1 }}
                    value={getVisualTabIndex(tabValue)}
                    onChange={handleTabValueChange}
                    orientation={isSmallScreen ? 'vertical' : 'horizontal'}
                    centered
                    indicatorColor="primary"
                >
                    {/* Tab 0: Organization Details */}
                    <Tab
                        label="Organization Details"
                        sx={{
                            flexGrow: 1,
                            border: isSmallScreen ? '1px solid' : 'none',
                            borderRadius: 0,
                            margin: isSmallScreen ? '5px' : '0',
                            maxWidth: isSmallScreen ? 'none' : 'initial',
                            backgroundColor:
                                isSmallScreen && tabValue === 0 ? 'primary.main' : 'none',
                            color: isSmallScreen && tabValue === 0 ? '#fff !important' : 'initial',
                        }}
                    />
                    {/* Tab 1: Back Office */}
                    <Tab
                        label={
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <OfficeDeskIcon
                                    style={{ marginRight: '4px', width: '20px', height: '20px' }}
                                />
                                Back Office
                            </div>
                        }
                        sx={{
                            flexGrow: 1,
                            border: isSmallScreen ? '1px solid' : 'none',
                            borderRadius: 0,
                            margin: isSmallScreen ? '5px' : '0',
                            maxWidth: isSmallScreen ? 'none' : 'initial',
                            backgroundColor:
                                isSmallScreen && tabValue === 6 ? 'primary.main' : 'none',
                            color: isSmallScreen && tabValue === 6 ? '#fff !important' : '#3F4953',
                        }}
                    />
                    {/* Tab 2: Invoices */}
                    <Tab
                        label={
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <ReceiptLongIcon style={{ marginRight: '4px' }} />
                                Invoices
                            </div>
                        }
                        sx={{
                            flexGrow: 1,
                            border: isSmallScreen ? '1px solid' : 'none',
                            borderRadius: 0,
                            margin: isSmallScreen ? '5px' : '0',
                            maxWidth: isSmallScreen ? 'none' : 'initial',
                            backgroundColor:
                                isSmallScreen && tabValue === 7 ? 'primary.main' : 'none',
                            color: isSmallScreen && tabValue === 7 ? '#fff !important' : '#3F4953',
                        }}
                    />
                    {/* Tab 3: Pods */}
                    <Tab
                        label={
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <SpeakerPhone style={{ marginRight: '8px' }} />
                                Pods
                            </div>
                        }
                        sx={{
                            flexGrow: 1,
                            border: isSmallScreen ? '1px solid' : 'none',
                            borderRadius: 0,
                            margin: isSmallScreen ? '5px' : '0',
                            maxWidth: isSmallScreen ? 'none' : 'initial',
                            backgroundColor:
                                isSmallScreen && tabValue === 5 ? 'primary.main' : 'none',
                            color: isSmallScreen && tabValue === 5 ? '#fff !important' : 'initial',
                        }}
                    />
                    {/* Tab 4: 3rd Party Integrations (dropdown) */}
                    <Tab
                        label="3rd Party Integrations"
                        sx={{
                            flexGrow: 1,
                            border: isSmallScreen ? '1px solid' : 'none',
                            borderRadius: 0,
                            margin: isSmallScreen ? '5px' : '0',
                            maxWidth: isSmallScreen ? 'none' : 'initial',
                            backgroundColor:
                                isSmallScreen &&
                                (tabValue === 1 ||
                                    tabValue === 2 ||
                                    tabValue === 3 ||
                                    tabValue === 4 ||
                                    tabValue === 8)
                                    ? 'primary.main'
                                    : 'none',
                            color:
                                isSmallScreen &&
                                (tabValue === 1 ||
                                    tabValue === 2 ||
                                    tabValue === 3 ||
                                    tabValue === 4 ||
                                    tabValue === 8)
                                    ? '#fff !important'
                                    : '#3F4953',
                        }}
                    />
                </Tabs>
                {/* Dropdown Menu for 3rd Party Integrations */}
                <Menu
                    anchorEl={integrationsMenuAnchor}
                    open={Boolean(integrationsMenuAnchor)}
                    onClose={() => handleIntegrationsMenuClose()}
                >
                    <MenuItem onClick={() => handleIntegrationsMenuClose(1)}>
                        Third Party Apps
                    </MenuItem>
                    <MenuItem onClick={() => handleIntegrationsMenuClose(2)}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <WorkspacePremiumIcon style={{ marginRight: '4px' }} />
                            Premium Features
                        </div>
                    </MenuItem>
                    <MenuItem onClick={() => handleIntegrationsMenuClose(3)}>SQL Access</MenuItem>
                    <MenuItem onClick={() => handleIntegrationsMenuClose(4)}>
                        {hasStripe ? (
                            'Marketplace'
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                {'Setup '}
                                <StripeIcon
                                    style={{
                                        marginRight: '4px',
                                        marginLeft: '4px',
                                        width: '45px',
                                        height: '40px',
                                    }}
                                />
                            </div>
                        )}
                    </MenuItem>
                    <MenuItem onClick={() => handleIntegrationsMenuClose(8)}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <LocalOffer style={{ marginRight: '4px' }} />
                            Attributes
                        </div>
                    </MenuItem>
                </Menu>
            </Grid>

            {/* Slug Change Confirmation Modal */}
            <Dialog
                open={showSlugConfirmModal}
                onClose={() => handleSlugConfirmation(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirm URL Change</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You are about to change your organization&apos;s URL slug from &quot;
                        {originalSlug}&quot; to &quot;{slug}&quot;. This will update your Fiyrpod
                        Store URL:
                    </DialogContentText>
                    <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                        • Fiyrpod Store URL: https://{slug}.fiyrpod.com
                    </Typography>
                    <DialogContentText sx={{ mt: 2 }}>
                        Any existing links with the old URL will no longer work. Are you sure you
                        want to proceed?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleSlugConfirmation(false)} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleSlugConfirmation(true)}
                        color="primary"
                        variant="contained"
                        disabled={saving}
                    >
                        {saving ? 'Updating...' : 'Update URL'}
                    </Button>
                </DialogActions>
            </Dialog>

            {tabValue === 0 && (
                <Grid
                    container
                    spacing={4}
                    sx={(theme) => ({
                        width: '100%',
                        maxWidth: theme.breakpoints.values.lg,
                        margin: '0 auto', // Center the grid
                        [theme.breakpoints.down('lg')]: {
                            maxWidth: '100%',
                        },
                    })}
                >
                    {organization && (
                        <React.Fragment>
                            {/* Share Links Section */}
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        marginTop: theme.spacing(6),
                                    }}
                                >
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            textAlign: 'center',
                                            fontWeight: 'bold',
                                            color: theme.palette.primary.main,
                                            mb: 1,
                                        }}
                                    >
                                        Share My Links!
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            textAlign: 'center',
                                            color: 'text.secondary',
                                            mb: 3,
                                        }}
                                    >
                                        Share these links to onboard athletes and coaches
                                    </Typography>
                                    {!slug && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                textAlign: 'center',
                                                padding: theme.spacing(0, 3, 2, 3),
                                                fontStyle: 'italic',
                                            }}
                                        >
                                            Set a custom URL slug below and save to generate your
                                            links
                                        </Typography>
                                    )}
                                    <Grid container spacing={3}>
                                        {/* Athlete Onboarding Link */}
                                        <Grid item xs={12} md={6}>
                                            <Paper
                                                elevation={2}
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-start',
                                                    padding: '28px 28px 32px',
                                                    border: `1px solid ${theme.palette.divider}`,
                                                    borderRadius: 3,
                                                    height: '100%',
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        textAlign: 'left',
                                                        fontWeight: 'bold',
                                                        color: theme.palette.text.primary,
                                                        marginBottom: '8px',
                                                    }}
                                                >
                                                    Invite Athletes
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        textAlign: 'left',
                                                        color: 'text.secondary',
                                                        marginBottom: '20px',
                                                        flex: 1,
                                                    }}
                                                >
                                                    Share with athletes to join your organization
                                                    and purchase a pod through the FiyrPod Athlete
                                                    App
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: 'text.secondary',
                                                        marginBottom: '8px',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px',
                                                    }}
                                                >
                                                    What they&apos;ll do:
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        marginBottom: '24px',
                                                        flexWrap: 'wrap',
                                                    }}
                                                >
                                                    {[
                                                        'Download App',
                                                        'Create Account',
                                                        'Join Org',
                                                        'Buy Pod',
                                                    ].map((label, i, arr) => (
                                                        <React.Fragment key={label}>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '6px',
                                                                    backgroundColor:
                                                                        theme.palette.grey[100],
                                                                    borderRadius: '20px',
                                                                    padding: '4px 10px',
                                                                }}
                                                            >
                                                                <Typography
                                                                    component="span"
                                                                    sx={{
                                                                        fontWeight: 700,
                                                                        fontSize: '0.65rem',
                                                                        color: theme.palette.text
                                                                            .secondary,
                                                                        backgroundColor:
                                                                            theme.palette.grey[300],
                                                                        borderRadius: '50%',
                                                                        width: '16px',
                                                                        height: '16px',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        flexShrink: 0,
                                                                        lineHeight: 1,
                                                                    }}
                                                                >
                                                                    {i + 1}
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        fontSize: '0.65rem',
                                                                        color: theme.palette.text
                                                                            .secondary,
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    {label}
                                                                </Typography>
                                                            </Box>
                                                            {i < arr.length - 1 && (
                                                                <ArrowForward
                                                                    sx={{
                                                                        fontSize: 14,
                                                                        color: theme.palette.text
                                                                            .disabled,
                                                                    }}
                                                                />
                                                            )}
                                                        </React.Fragment>
                                                    ))}
                                                </Box>
                                                <TextField
                                                    label="Invite Athletes URL"
                                                    variant="outlined"
                                                    value={
                                                        slug
                                                            ? athleteOnboardingUrl
                                                            : 'Set a custom URL slug below'
                                                    }
                                                    InputProps={{
                                                        readOnly: true,
                                                        sx: {
                                                            fontSize: '1rem',
                                                            fontWeight: 500,
                                                        },
                                                        endAdornment: slug ? (
                                                            <InputAdornment position="end">
                                                                <Tooltip
                                                                    title={
                                                                        copySuccessAthlete
                                                                            ? 'Copied!'
                                                                            : 'Copy URL'
                                                                    }
                                                                >
                                                                    <IconButton
                                                                        aria-label="copy athlete onboarding url"
                                                                        onClick={() =>
                                                                            copyAthleteUrlToClipboard()
                                                                        }
                                                                        edge="end"
                                                                        size="large"
                                                                        sx={{
                                                                            color: theme.palette
                                                                                .primary.main,
                                                                            '&:hover': {
                                                                                backgroundColor: `${theme.palette.primary.main}15`,
                                                                            },
                                                                        }}
                                                                    >
                                                                        <ContentCopyIcon fontSize="medium" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </InputAdornment>
                                                        ) : undefined,
                                                    }}
                                                    sx={{
                                                        width: '100%',
                                                        marginBottom: '20px',
                                                        '& .MuiOutlinedInput-root': {
                                                            fontSize: '0.9rem',
                                                            backgroundColor: 'white',
                                                            '&:hover': {
                                                                '& .MuiOutlinedInput-notchedOutline':
                                                                    {
                                                                        borderColor:
                                                                            theme.palette.primary
                                                                                .main,
                                                                    },
                                                            },
                                                        },
                                                    }}
                                                />
                                                {slug && (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 2,
                                                            width: '100%',
                                                            flexDirection: {
                                                                xs: 'column',
                                                                sm: 'row',
                                                            },
                                                        }}
                                                    >
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            onClick={() => {
                                                                window.open(
                                                                    athleteOnboardingUrl,
                                                                    '_blank',
                                                                    'noopener,noreferrer',
                                                                );
                                                            }}
                                                            sx={{
                                                                flex: 1,
                                                                height: '40px',
                                                                fontSize: '0.85rem',
                                                                fontWeight: 600,
                                                                textTransform: 'none',
                                                                boxShadow: 2,
                                                                '&:hover': {
                                                                    boxShadow: 4,
                                                                },
                                                            }}
                                                        >
                                                            Preview Athlete Invite
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={<QrCode2Icon />}
                                                            onClick={handleClickOpenQRAthlete}
                                                            sx={{
                                                                flex: 1,
                                                                height: '40px',
                                                                fontSize: '0.85rem',
                                                                fontWeight: 600,
                                                                textTransform: 'none',
                                                                borderColor: '#000',
                                                                color: '#000',
                                                                '&:hover': {
                                                                    borderColor: '#000',
                                                                    backgroundColor: '#0000000a',
                                                                },
                                                            }}
                                                        >
                                                            QR Code
                                                        </Button>
                                                    </Box>
                                                )}
                                            </Paper>
                                        </Grid>

                                        {/* Coach/Organization Store Link */}
                                        <Grid item xs={12} md={6}>
                                            <Paper
                                                elevation={2}
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'flex-start',
                                                    padding: '28px 28px 32px',
                                                    border: `1px solid ${theme.palette.divider}`,
                                                    borderRadius: 3,
                                                    height: '100%',
                                                }}
                                            >
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        textAlign: 'left',
                                                        fontWeight: 'bold',
                                                        color: theme.palette.text.primary,
                                                        marginBottom: '8px',
                                                    }}
                                                >
                                                    Fiyrpod Store — Athletes, Coaches & Orgs
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        textAlign: 'left',
                                                        color: 'text.secondary',
                                                        marginBottom: '20px',
                                                        flex: 1,
                                                    }}
                                                >
                                                    Direct link to the Fiyrpod store for purchasing
                                                    pods and subscriptions
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: 'text.secondary',
                                                        marginBottom: '8px',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px',
                                                    }}
                                                >
                                                    What they&apos;ll do:
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        marginBottom: '24px',
                                                        flexWrap: 'wrap',
                                                    }}
                                                >
                                                    {[
                                                        'Open Store',
                                                        'Pick Subscription',
                                                        'Purchase',
                                                        'Download App',
                                                    ].map((label, i, arr) => (
                                                        <React.Fragment key={label}>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '6px',
                                                                    backgroundColor:
                                                                        theme.palette.grey[100],
                                                                    borderRadius: '20px',
                                                                    padding: '4px 10px',
                                                                }}
                                                            >
                                                                <Typography
                                                                    component="span"
                                                                    sx={{
                                                                        fontWeight: 700,
                                                                        fontSize: '0.65rem',
                                                                        color: theme.palette.text
                                                                            .secondary,
                                                                        backgroundColor:
                                                                            theme.palette.grey[300],
                                                                        borderRadius: '50%',
                                                                        width: '16px',
                                                                        height: '16px',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        flexShrink: 0,
                                                                        lineHeight: 1,
                                                                    }}
                                                                >
                                                                    {i + 1}
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        fontSize: '0.65rem',
                                                                        color: theme.palette.text
                                                                            .secondary,
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    {label}
                                                                </Typography>
                                                            </Box>
                                                            {i < arr.length - 1 && (
                                                                <ArrowForward
                                                                    sx={{
                                                                        fontSize: 14,
                                                                        color: theme.palette.text
                                                                            .disabled,
                                                                    }}
                                                                />
                                                            )}
                                                        </React.Fragment>
                                                    ))}
                                                </Box>
                                                <TextField
                                                    label="Fiyrpod Store URL"
                                                    variant="outlined"
                                                    value={
                                                        slug
                                                            ? fiyrpodStoreUrl
                                                            : 'Set a custom URL slug below'
                                                    }
                                                    InputProps={{
                                                        readOnly: true,
                                                        sx: {
                                                            fontSize: '1rem',
                                                            fontWeight: 500,
                                                        },
                                                        endAdornment: slug ? (
                                                            <InputAdornment position="end">
                                                                <Tooltip
                                                                    title={
                                                                        copySuccess
                                                                            ? 'Copied!'
                                                                            : 'Copy URL'
                                                                    }
                                                                >
                                                                    <IconButton
                                                                        aria-label="copy fiyrpod url"
                                                                        onClick={() =>
                                                                            copyToClipboard()
                                                                        }
                                                                        edge="end"
                                                                        size="large"
                                                                        sx={{
                                                                            color: theme.palette
                                                                                .primary.main,
                                                                            '&:hover': {
                                                                                backgroundColor: `${theme.palette.primary.main}15`,
                                                                            },
                                                                        }}
                                                                    >
                                                                        <ContentCopyIcon fontSize="medium" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </InputAdornment>
                                                        ) : undefined,
                                                    }}
                                                    sx={{
                                                        width: '100%',
                                                        marginBottom: '20px',
                                                        '& .MuiOutlinedInput-root': {
                                                            fontSize: '0.9rem',
                                                            backgroundColor: 'white',
                                                            '&:hover': {
                                                                '& .MuiOutlinedInput-notchedOutline':
                                                                    {
                                                                        borderColor:
                                                                            theme.palette.primary
                                                                                .main,
                                                                    },
                                                            },
                                                        },
                                                    }}
                                                />
                                                {slug && (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 2,
                                                            width: '100%',
                                                            flexDirection: {
                                                                xs: 'column',
                                                                sm: 'row',
                                                            },
                                                        }}
                                                    >
                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            onClick={() => {
                                                                window.open(
                                                                    fiyrpodStoreUrl,
                                                                    '_blank',
                                                                    'noopener,noreferrer',
                                                                );
                                                            }}
                                                            sx={{
                                                                flex: 1,
                                                                height: '40px',
                                                                fontSize: '0.85rem',
                                                                fontWeight: 600,
                                                                textTransform: 'none',
                                                                boxShadow: 2,
                                                                '&:hover': {
                                                                    boxShadow: 4,
                                                                },
                                                            }}
                                                        >
                                                            Go to Store
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={<QrCode2Icon />}
                                                            onClick={handleClickOpenQR}
                                                            sx={{
                                                                flex: 1,
                                                                height: '40px',
                                                                fontSize: '0.85rem',
                                                                fontWeight: 600,
                                                                textTransform: 'none',
                                                                borderColor: '#000',
                                                                color: '#000',
                                                                '&:hover': {
                                                                    borderColor: '#000',
                                                                    backgroundColor: '#0000000a',
                                                                },
                                                            }}
                                                        >
                                                            QR Code
                                                        </Button>
                                                    </Box>
                                                )}
                                            </Paper>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>

                            {/* Prominent Primary Organization Section - Second Most Important */}
                            <Grid item xs={12}>
                                <Paper
                                    elevation={8}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        marginTop: theme.spacing(6),
                                        padding: theme.spacing(4),
                                        background: `linear-gradient(135deg, ${theme.palette.secondary.main}15 0%, ${theme.palette.secondary.light}08 100%)`,
                                        border: `2px solid ${theme.palette.secondary.main}40`,
                                        borderRadius: 3,
                                        position: 'relative',
                                        overflow: 'visible',
                                    }}
                                >
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            textAlign: 'center',
                                            padding: theme.spacing(2, 3),
                                            fontWeight: 'bold',
                                            color: theme.palette.secondary.main,
                                            mb: 2,
                                        }}
                                    >
                                        ⭐ Set as my Primary Organization
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            textAlign: 'center',
                                            padding: theme.spacing(0, 3, 3, 3),
                                            color: 'text.secondary',
                                            mb: 3,
                                            maxWidth: '700px',
                                        }}
                                    >
                                        Your primary organization will be automatically selected in
                                        filters and forms throughout the app, making it faster to
                                        create groups, view data, and manage your most important
                                        organization.{' '}
                                        {hiddenOrganizationUuids?.includes(
                                            organization?.uuid || '',
                                        ) && (
                                            <strong>
                                                Note: This organization is currently hidden. Setting
                                                it as primary will automatically make it visible in
                                                filters.
                                            </strong>
                                        )}
                                    </Typography>
                                    <FormControl
                                        sx={{
                                            width: '100%',
                                            maxWidth: '500px',
                                            padding: theme.spacing(2),
                                            background: theme.palette.background.paper,
                                            borderRadius: 2,
                                            border: `2px solid ${
                                                organization?.uuid === primaryOrganizationUuid ||
                                                `${organization?.name}${organization?.uuid}` ===
                                                    favOrg
                                                    ? theme.palette.secondary.main
                                                    : 'transparent'
                                            }`,
                                            transition: 'all 0.3s ease',
                                            mb: 3,
                                        }}
                                    >
                                        <Tooltip
                                            title={
                                                hiddenOrganizationUuids?.includes(
                                                    organization?.uuid || '',
                                                ) && organization?.uuid !== primaryOrganizationUuid
                                                    ? 'Hidden organizations cannot be set as primary. Unhide this organization first.'
                                                    : ''
                                            }
                                            arrow
                                        >
                                            <span>
                                                <FormControlLabel
                                                    checked={
                                                        organization?.uuid ===
                                                            primaryOrganizationUuid ||
                                                        `${organization?.name}${organization?.uuid}` ===
                                                            favOrg
                                                    }
                                                    onChange={handlePrimaryOrgChange}
                                                    disabled={
                                                        hiddenOrganizationUuids?.includes(
                                                            organization?.uuid || '',
                                                        ) &&
                                                        organization?.uuid !==
                                                            primaryOrganizationUuid
                                                    }
                                                    control={
                                                        <Checkbox
                                                            sx={{
                                                                color: theme.palette.secondary.main,
                                                                '&.Mui-checked': {
                                                                    color: theme.palette.secondary
                                                                        .main,
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography
                                                            sx={{
                                                                fontSize: '1rem',
                                                                fontWeight:
                                                                    organization?.uuid ===
                                                                        primaryOrganizationUuid ||
                                                                    `${organization?.name}${organization?.uuid}` ===
                                                                        favOrg
                                                                        ? 700
                                                                        : 400,
                                                            }}
                                                        >
                                                            {organization?.uuid ===
                                                                primaryOrganizationUuid ||
                                                            `${organization?.name}${organization?.uuid}` ===
                                                                favOrg ? (
                                                                <>
                                                                    ✓{' '}
                                                                    <strong>
                                                                        {organization?.name}
                                                                    </strong>{' '}
                                                                    is your Primary Organization
                                                                </>
                                                            ) : (
                                                                <>
                                                                    Set{' '}
                                                                    <strong>
                                                                        {organization?.name}
                                                                    </strong>{' '}
                                                                    as my primary throughout app
                                                                </>
                                                            )}
                                                        </Typography>
                                                    }
                                                    sx={{
                                                        padding: theme.spacing(1.5),
                                                        borderRadius: 1,
                                                        width: '100%',
                                                        '&:hover': {
                                                            backgroundColor: `${theme.palette.secondary.main}08`,
                                                        },
                                                    }}
                                                />
                                            </span>
                                        </Tooltip>
                                    </FormControl>
                                </Paper>
                            </Grid>

                            {/* Prominent Hide Organization Section - Right Below Primary */}
                            <Grid item xs={12}>
                                <Paper
                                    elevation={8}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        marginTop: theme.spacing(6),
                                        padding: theme.spacing(4),
                                        background: `linear-gradient(135deg, ${theme.palette.info.main}15 0%, ${theme.palette.info.light}08 100%)`,
                                        border: `2px solid ${theme.palette.info.main}40`,
                                        borderRadius: 3,
                                        position: 'relative',
                                        overflow: 'visible',
                                    }}
                                >
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            textAlign: 'center',
                                            padding: theme.spacing(2, 3),
                                            fontWeight: 'bold',
                                            color: theme.palette.info.main,
                                            mb: 2,
                                        }}
                                    >
                                        👁️ Hide Organization from Filters
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            textAlign: 'center',
                                            padding: theme.spacing(0, 3, 3, 3),
                                            color: 'text.secondary',
                                            mb: 3,
                                            maxWidth: '700px',
                                        }}
                                    >
                                        Hide this organization from the filter dropdowns on Groups
                                        and Individuals pages. This keeps your interface clean and
                                        focused on the organizations you use most often.{' '}
                                        {organization?.uuid === primaryOrganizationUuid && (
                                            <strong>
                                                Note: Hiding this organization will automatically
                                                unset it as your primary organization.
                                            </strong>
                                        )}
                                    </Typography>
                                    <FormControl
                                        sx={{
                                            width: '100%',
                                            maxWidth: '500px',
                                            padding: theme.spacing(2),
                                            background: theme.palette.background.paper,
                                            borderRadius: 2,
                                            border: `2px solid ${
                                                organization?.uuid &&
                                                hiddenOrganizationUuids?.includes(organization.uuid)
                                                    ? theme.palette.info.main
                                                    : 'transparent'
                                            }`,
                                            transition: 'all 0.3s ease',
                                            mb: 3,
                                        }}
                                    >
                                        <Tooltip
                                            title={
                                                organization?.uuid === primaryOrganizationUuid
                                                    ? 'Hiding this organization will automatically unset it as your primary organization.'
                                                    : ''
                                            }
                                            arrow
                                        >
                                            <span>
                                                <FormControlLabel
                                                    checked={
                                                        organization?.uuid
                                                            ? hiddenOrganizationUuids?.includes(
                                                                  organization.uuid,
                                                              )
                                                            : false
                                                    }
                                                    onChange={handleHideOrganizationChange}
                                                    control={
                                                        <Checkbox
                                                            sx={{
                                                                color: theme.palette.info.main,
                                                                '&.Mui-checked': {
                                                                    color: theme.palette.info.main,
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography
                                                            sx={{
                                                                fontSize: '1rem',
                                                                fontWeight:
                                                                    organization?.uuid &&
                                                                    hiddenOrganizationUuids?.includes(
                                                                        organization.uuid,
                                                                    )
                                                                        ? 700
                                                                        : 400,
                                                            }}
                                                        >
                                                            {organization?.uuid &&
                                                            hiddenOrganizationUuids?.includes(
                                                                organization.uuid,
                                                            ) ? (
                                                                <>
                                                                    ✓{' '}
                                                                    <strong>
                                                                        {organization?.name}
                                                                    </strong>{' '}
                                                                    is hidden from filters
                                                                </>
                                                            ) : (
                                                                <>
                                                                    Hide{' '}
                                                                    <strong>
                                                                        {organization?.name}
                                                                    </strong>{' '}
                                                                    from Groups and Individual
                                                                    filters
                                                                </>
                                                            )}
                                                        </Typography>
                                                    }
                                                    sx={{
                                                        padding: theme.spacing(1.5),
                                                        borderRadius: 1,
                                                        width: '100%',
                                                        '&:hover': {
                                                            backgroundColor: `${theme.palette.info.main}08`,
                                                        },
                                                    }}
                                                />
                                            </span>
                                        </Tooltip>
                                    </FormControl>
                                </Paper>
                            </Grid>

                            {/* QR Code Modal */}
                            <Dialog
                                open={openQR}
                                onClose={handleCloseQR}
                                maxWidth="sm"
                                fullWidth
                                PaperProps={{
                                    sx: {
                                        backgroundColor: '#0a0a0a',
                                        color: '#FFFFFF',
                                        borderRadius: 3,
                                        position: 'relative',
                                        overflow: 'hidden',
                                        background: `
                                            radial-gradient(ellipse at top left, rgba(0, 255, 0, 0.1) 0%, transparent 50%),
                                            radial-gradient(ellipse at bottom right, rgba(0, 150, 255, 0.1) 0%, transparent 50%),
                                            radial-gradient(ellipse at top right, rgba(150, 0, 255, 0.1) 0%, transparent 50%),
                                            #0a0a0a
                                        `,
                                        border: '4px solid',
                                        borderImage: `
                                            linear-gradient(
                                                135deg,
                                                #00FF00 0%,
                                                #00CCFF 25%,
                                                #9900FF 50%,
                                                #FF0099 75%,
                                                #00FF00 100%
                                            ) 1
                                        `,
                                        boxShadow: `
                                            0 0 30px rgba(0, 255, 0, 0.3),
                                            inset 0 0 30px rgba(0, 255, 0, 0.05)
                                        `,
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background: `
                                                linear-gradient(45deg, transparent 30%, rgba(0, 255, 0, 0.05) 50%, transparent 70%),
                                                linear-gradient(-45deg, transparent 30%, rgba(0, 150, 255, 0.05) 50%, transparent 70%),
                                                linear-gradient(135deg, transparent 30%, rgba(150, 0, 255, 0.05) 50%, transparent 70%)
                                            `,
                                            backgroundSize: '200% 200%',
                                            animation: `${shimmerAnimation} 3s ease-in-out infinite`,
                                            pointerEvents: 'none',
                                            zIndex: 0,
                                        },
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'relative',
                                        zIndex: 1,
                                    }}
                                >
                                    <DialogTitle
                                        sx={{
                                            textAlign: 'center',
                                            padding: theme.spacing(2, 3, 1, 3),
                                            borderBottom: 'none',
                                            background: 'transparent',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={FiyrPodLogo}
                                            alt="FIYRPOD"
                                            sx={{
                                                height: '40px',
                                                width: 'auto',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </DialogTitle>
                                    <Box
                                        sx={{
                                            textAlign: 'center',
                                            padding: theme.spacing(0, 3, 2, 3),
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'inline-block',
                                                padding: theme.spacing(1, 3),
                                                borderRadius: 1,
                                                border: '2px solid #00FF00',
                                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                backdropFilter: 'blur(10px)',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: '1.2rem',
                                                    fontWeight: 'bold',
                                                    color: '#FFFFFF',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '1px',
                                                }}
                                            >
                                                {organization?.name || 'Organization'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Typography
                                        sx={{
                                            textAlign: 'center',
                                            padding: theme.spacing(1, 3, 2, 3),
                                            fontSize: '0.9rem',
                                            fontWeight: 'normal',
                                            color: '#FFFFFF',
                                            textTransform: 'uppercase',
                                            letterSpacing: '2px',
                                        }}
                                    >
                                        SCAN TO JOIN
                                    </Typography>
                                    <DialogContent
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            padding: theme.spacing(2, 4, 2, 4),
                                            background: 'transparent',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                backgroundColor: '#FFFFFF',
                                                padding: theme.spacing(3),
                                                borderRadius: 2,
                                                marginBottom: theme.spacing(2),
                                                boxShadow: '0 0 30px rgba(0, 255, 0, 0.6)',
                                                border: '3px solid #00FF00',
                                            }}
                                        >
                                            <QRCode
                                                size={256}
                                                style={{
                                                    height: 'auto',
                                                    maxWidth: '100%',
                                                    width: '100%',
                                                }}
                                                value={fiyrpodStoreUrl}
                                                viewBox="0 0 256 256"
                                            />
                                        </Box>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                textAlign: 'center',
                                                color: '#00FF00',
                                                fontWeight: 'bold',
                                                marginTop: theme.spacing(1),
                                                textShadow: '0 0 15px rgba(0, 255, 0, 0.8)',
                                                fontSize: '1rem',
                                            }}
                                        >
                                            Fiyrpod Store — Athletes, Coaches & Orgs
                                        </Typography>
                                    </DialogContent>
                                    <DialogActions
                                        sx={{
                                            justifyContent: 'center',
                                            padding: theme.spacing(2, 3, 3, 3),
                                            borderTop: 'none',
                                            background: 'transparent',
                                        }}
                                    >
                                        <Button
                                            onClick={handleCloseQR}
                                            variant="outlined"
                                            sx={{
                                                borderColor: '#00FF00',
                                                borderWidth: '2px',
                                                color: '#FFFFFF',
                                                fontWeight: 'bold',
                                                padding: theme.spacing(1.5, 4),
                                                textTransform: 'uppercase',
                                                letterSpacing: '2px',
                                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                backdropFilter: 'blur(10px)',
                                                boxShadow: '0 0 15px rgba(0, 255, 0, 0.3)',
                                                '&:hover': {
                                                    borderColor: '#00FF00',
                                                    backgroundColor: 'rgba(0, 255, 0, 0.1)',
                                                    boxShadow: '0 0 25px rgba(0, 255, 0, 0.5)',
                                                },
                                            }}
                                        >
                                            LET&apos;S GO
                                        </Button>
                                    </DialogActions>
                                </Box>
                            </Dialog>

                            {/* Athlete Onboarding QR Code Modal */}
                            <Dialog
                                open={openQRAthlete}
                                onClose={handleCloseQRAthlete}
                                maxWidth="sm"
                                fullWidth
                                PaperProps={{
                                    sx: {
                                        backgroundColor: '#0a0a0a',
                                        color: '#FFFFFF',
                                        borderRadius: 3,
                                        position: 'relative',
                                        overflow: 'hidden',
                                        background: `
                                            radial-gradient(ellipse at top left, rgba(0, 255, 0, 0.1) 0%, transparent 50%),
                                            radial-gradient(ellipse at bottom right, rgba(0, 150, 255, 0.1) 0%, transparent 50%),
                                            radial-gradient(ellipse at top right, rgba(150, 0, 255, 0.1) 0%, transparent 50%),
                                            #0a0a0a
                                        `,
                                        border: '4px solid',
                                        borderImage: `
                                            linear-gradient(
                                                135deg,
                                                #00FF00 0%,
                                                #00CCFF 25%,
                                                #9900FF 50%,
                                                #FF0099 75%,
                                                #00FF00 100%
                                            ) 1
                                        `,
                                        boxShadow: `
                                            0 0 30px rgba(0, 255, 0, 0.3),
                                            inset 0 0 30px rgba(0, 255, 0, 0.05)
                                        `,
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            background: `
                                                linear-gradient(45deg, transparent 30%, rgba(0, 255, 0, 0.05) 50%, transparent 70%),
                                                linear-gradient(-45deg, transparent 30%, rgba(0, 150, 255, 0.05) 50%, transparent 70%),
                                                linear-gradient(135deg, transparent 30%, rgba(150, 0, 255, 0.05) 50%, transparent 70%)
                                            `,
                                            backgroundSize: '200% 200%',
                                            animation: `${shimmerAnimation} 3s ease-in-out infinite`,
                                            pointerEvents: 'none',
                                            zIndex: 0,
                                        },
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'relative',
                                        zIndex: 1,
                                    }}
                                >
                                    <DialogTitle
                                        sx={{
                                            textAlign: 'center',
                                            padding: theme.spacing(2, 3, 1, 3),
                                            borderBottom: 'none',
                                            background: 'transparent',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={FiyrPodLogo}
                                            alt="FIYRPOD"
                                            sx={{
                                                height: '40px',
                                                width: 'auto',
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </DialogTitle>
                                    <Box
                                        sx={{
                                            textAlign: 'center',
                                            padding: theme.spacing(0, 3, 2, 3),
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'inline-block',
                                                padding: theme.spacing(1, 3),
                                                borderRadius: 1,
                                                border: '2px solid #00FF00',
                                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                backdropFilter: 'blur(10px)',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: '1.2rem',
                                                    fontWeight: 'bold',
                                                    color: '#FFFFFF',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '1px',
                                                }}
                                            >
                                                {organization?.name || 'Organization'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Typography
                                        sx={{
                                            textAlign: 'center',
                                            padding: theme.spacing(1, 3, 2, 3),
                                            fontSize: '0.9rem',
                                            fontWeight: 'normal',
                                            color: '#FFFFFF',
                                            textTransform: 'uppercase',
                                            letterSpacing: '2px',
                                        }}
                                    >
                                        SCAN TO JOIN
                                    </Typography>
                                    <DialogContent
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            padding: theme.spacing(2, 4, 2, 4),
                                            background: 'transparent',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                backgroundColor: '#FFFFFF',
                                                padding: theme.spacing(3),
                                                borderRadius: 2,
                                                marginBottom: theme.spacing(2),
                                                boxShadow: '0 0 30px rgba(0, 255, 0, 0.6)',
                                                border: '3px solid #00FF00',
                                            }}
                                        >
                                            <QRCode
                                                size={256}
                                                style={{
                                                    height: 'auto',
                                                    maxWidth: '100%',
                                                    width: '100%',
                                                }}
                                                value={athleteOnboardingUrl}
                                                viewBox="0 0 256 256"
                                            />
                                        </Box>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                textAlign: 'center',
                                                color: '#00FF00',
                                                fontWeight: 'bold',
                                                marginTop: theme.spacing(1),
                                                textShadow: '0 0 15px rgba(0, 255, 0, 0.8)',
                                                fontSize: '1rem',
                                            }}
                                        >
                                            Invite Athletes
                                        </Typography>
                                    </DialogContent>
                                    <DialogActions
                                        sx={{
                                            justifyContent: 'center',
                                            padding: theme.spacing(2, 3, 3, 3),
                                            borderTop: 'none',
                                            background: 'transparent',
                                        }}
                                    >
                                        <Button
                                            onClick={handleCloseQRAthlete}
                                            variant="outlined"
                                            sx={{
                                                borderColor: '#00FF00',
                                                borderWidth: '2px',
                                                color: '#FFFFFF',
                                                fontWeight: 'bold',
                                                padding: theme.spacing(1.5, 4),
                                                textTransform: 'uppercase',
                                                letterSpacing: '2px',
                                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                backdropFilter: 'blur(10px)',
                                                boxShadow: '0 0 15px rgba(0, 255, 0, 0.3)',
                                                '&:hover': {
                                                    borderColor: '#00FF00',
                                                    backgroundColor: 'rgba(0, 255, 0, 0.1)',
                                                    boxShadow: '0 0 25px rgba(0, 255, 0, 0.5)',
                                                },
                                            }}
                                        >
                                            LET&apos;S GO
                                        </Button>
                                    </DialogActions>
                                </Box>
                            </Dialog>

                            <Grid item xs={12} lg={6}>
                                <Paper
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        marginTop: theme.spacing(6),
                                    }}
                                >
                                    <Typography
                                        sx={{ textAlign: 'center', padding: 3 }}
                                        fontWeight="bold"
                                    >
                                        Make sure your organization name is up to date.
                                    </Typography>
                                    <TextField
                                        label="Organization Name"
                                        id="name"
                                        name="name"
                                        variant="outlined"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        sx={{
                                            width: '98%',
                                            margin: theme.spacing(6, 3),
                                        }}
                                    />
                                </Paper>
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <Paper
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        marginTop: theme.spacing(6),
                                    }}
                                >
                                    <Typography
                                        sx={{ textAlign: 'center', padding: 3 }}
                                        fontWeight="bold"
                                    >
                                        Let others know where you are located to help build your
                                        community
                                    </Typography>
                                    <TextField
                                        label="Zip Code"
                                        id="zip"
                                        name="zip"
                                        variant="outlined"
                                        value={zip}
                                        onChange={(e) => setZip(e.target.value)}
                                        sx={{
                                            width: '98%',
                                            margin: theme.spacing(6, 3),
                                        }}
                                    />
                                </Paper>
                            </Grid>
                            <Grid item xs={12} lg={6}>
                                <Paper
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        marginTop: theme.spacing(6),
                                    }}
                                >
                                    <Typography
                                        sx={{ textAlign: 'center', padding: 3 }}
                                        fontWeight="bold"
                                    >
                                        Customize your 🔗 Links by changing your URL below
                                    </Typography>
                                    <TextField
                                        label="Custom Organization URL"
                                        id="slug"
                                        name="slug"
                                        variant="outlined"
                                        value={slug}
                                        onChange={(e) => setSlug(e.target.value)}
                                        sx={{
                                            width: '98%',
                                            margin: theme.spacing(6, 3),
                                        }}
                                    />
                                </Paper>
                            </Grid>

                            <Grid item xs={12} lg={6}>
                                <Paper
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        marginTop: theme.spacing(6),
                                        minHeight: '210px',
                                    }}
                                >
                                    <Typography
                                        sx={{ textAlign: 'center', padding: 3 }}
                                        fontWeight="bold"
                                    >
                                        Write a brief description of your specialites and background
                                        so athletes and other coaches can get to know you better.
                                    </Typography>
                                    <TextField
                                        multiline
                                        rows={4}
                                        label="About Us"
                                        id="about_us"
                                        name="about_us"
                                        variant="outlined"
                                        value={aboutUs}
                                        onChange={(e) => setAboutUs(e.target.value)}
                                        sx={{
                                            width: '98%',
                                            margin: theme.spacing(6, 3),
                                        }}
                                    />
                                </Paper>
                            </Grid>

                            <Grid item xs={12} lg={6}>
                                <Paper
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        marginTop: theme.spacing(6),
                                        minHeight: '210px',
                                    }}
                                >
                                    <Typography
                                        sx={{ textAlign: 'center', padding: 3 }}
                                        fontWeight="bold"
                                    >
                                        Organization Owner Information
                                    </Typography>
                                    <TextField
                                        label="Owner First Name"
                                        id="owner_first_name"
                                        name="owner_first_name"
                                        variant="outlined"
                                        value={ownerFirstName}
                                        onChange={(e) => setOwnerFirstName(e.target.value)}
                                        sx={{
                                            width: '98%',
                                            margin: theme.spacing(2, 3),
                                        }}
                                    />
                                    <TextField
                                        label="Owner Last Name"
                                        id="owner_last_name"
                                        name="owner_last_name"
                                        variant="outlined"
                                        value={ownerLastName}
                                        onChange={(e) => setOwnerLastName(e.target.value)}
                                        sx={{
                                            width: '98%',
                                            margin: theme.spacing(2, 3),
                                        }}
                                    />
                                </Paper>
                            </Grid>

                            <Grid item xs={12} lg={6}>
                                <Paper
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        marginTop: theme.spacing(6),
                                        minHeight: '210px',
                                    }}
                                >
                                    <Typography
                                        sx={{ textAlign: 'center', padding: 3 }}
                                        fontWeight="bold"
                                    >
                                        Owner Contact Information
                                    </Typography>
                                    <TextField
                                        label="Owner Email"
                                        id="owner_email"
                                        name="owner_email"
                                        type="email"
                                        variant="outlined"
                                        value={ownerEmail}
                                        onChange={(e) => setOwnerEmail(e.target.value)}
                                        sx={{
                                            width: '98%',
                                            margin: theme.spacing(2, 3),
                                        }}
                                    />
                                    <TextField
                                        label="Owner Phone"
                                        id="owner_phone"
                                        name="owner_phone"
                                        type="tel"
                                        variant="outlined"
                                        value={formatPhoneDisplay(ownerPhone)}
                                        onChange={(e) => {
                                            const input = e.target.value;
                                            // Only allow digits - extract and store digits only
                                            const digits = extractPhoneDigits(input);
                                            // If we have 11 digits starting with 1, remove the leading 1 (US country code)
                                            let normalizedDigits = digits;
                                            if (
                                                normalizedDigits.length === 11 &&
                                                normalizedDigits.startsWith('1')
                                            ) {
                                                normalizedDigits = normalizedDigits.substring(1);
                                            }
                                            // Limit to 10 digits (US phone number)
                                            const limitedDigits = normalizedDigits.substring(0, 10);
                                            setOwnerPhone(limitedDigits);
                                        }}
                                        onKeyDown={(e) => {
                                            // Allow: backspace, delete, tab, escape, enter, and arrow keys
                                            if (
                                                [8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(
                                                    e.keyCode,
                                                ) !== -1 ||
                                                // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                                                (e.keyCode === 65 && e.ctrlKey === true) ||
                                                (e.keyCode === 67 && e.ctrlKey === true) ||
                                                (e.keyCode === 86 && e.ctrlKey === true) ||
                                                (e.keyCode === 88 && e.ctrlKey === true)
                                            ) {
                                                return;
                                            }
                                            // Ensure that it is a number (0-9) and stop the keypress
                                            if (
                                                (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
                                                (e.keyCode < 96 || e.keyCode > 105)
                                            ) {
                                                e.preventDefault();
                                            }
                                        }}
                                        placeholder="(555) 123-4567"
                                        helperText="Enter 10-digit US phone number (numbers only, auto-formatted)"
                                        sx={{
                                            width: '98%',
                                            margin: theme.spacing(2, 3),
                                        }}
                                    />
                                    {hasOwnerFieldsChanged && (
                                        <Box
                                            sx={{
                                                width: '98%',
                                                margin: theme.spacing(2, 3),
                                                display: 'flex',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <ButtonWithLoader
                                                onClick={handleSaveOwnerFields}
                                                loading={saving}
                                                disabled={saving}
                                                variant="contained"
                                                color="primary"
                                                sx={{
                                                    minWidth: '120px',
                                                }}
                                            >
                                                Save Owner Info
                                            </ButtonWithLoader>
                                        </Box>
                                    )}
                                </Paper>
                            </Grid>

                            <Grid item xs={12} lg={6}>
                                <Paper
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        marginTop: theme.spacing(6),
                                        minHeight: '210px',
                                    }}
                                >
                                    <Typography
                                        sx={{ textAlign: 'center', padding: 3 }}
                                        fontWeight="bold"
                                    >
                                        Make sure your logo is up to date. Click ⬇️ on the logo to
                                        update.
                                    </Typography>
                                    <UploadLogo
                                        loading={logoUploading}
                                        button={false}
                                        deleteLogo={deleteLogo}
                                        uploadImage={uploadLogo}
                                        setUploadImage={setUploadLogo}
                                        defaultImgSrc={organization.logo_url}
                                        onMessage={setSnackMessage}
                                        onClose={async (croppedFile) => {
                                            // Auto-save logo after cropping
                                            if (croppedFile) {
                                                setUploadLogo(croppedFile);
                                                // Use setTimeout to ensure state is updated before saving
                                                setTimeout(async () => {
                                                    // Only save if slug hasn't changed (to avoid confirmation modal)
                                                    if (slug === originalSlug) {
                                                        try {
                                                            await performSave();
                                                            // Refresh organizations context to update navigation bar logo immediately
                                                            dispatch(shuffleOrganizationsLoadKey());
                                                            setSnackMessage(
                                                                'Logo updated successfully!',
                                                            );
                                                            setSuccess(true);
                                                        } catch (error) {
                                                            // If save fails, show message but don't break the UI
                                                            setSnackMessage(
                                                                'Logo cropped successfully. Please click Save to update organization.',
                                                            );
                                                            setSuccess(false);
                                                        }
                                                    } else {
                                                        // If slug changed, show success message but don't auto-save
                                                        // User will need to click main Save button
                                                        setSnackMessage(
                                                            'Logo cropped successfully. Click Save to update organization.',
                                                        );
                                                        setSuccess(true);
                                                    }
                                                }, 100);
                                            }
                                        }}
                                    />
                                </Paper>
                            </Grid>

                            <Grid item xs={12} lg={6}>
                                <Paper
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        minHeight: '240px',
                                        marginTop: theme.spacing(6),
                                    }}
                                >
                                    <Typography
                                        sx={{ textAlign: 'center', padding: 3 }}
                                        fontWeight="bold"
                                    >
                                        {organization.banner_url
                                            ? 'Your Marketplace Background Image'
                                            : 'Add a banner background to customize your marketplace'}
                                    </Typography>
                                    <UploadBanner
                                        loading={bannerUploading}
                                        button={false}
                                        deleteBanner={deleteBanner}
                                        uploadImage={uploadBanner}
                                        setUploadImage={setUploadBanner}
                                        defaultImgSrc={organization.banner_url}
                                        onMessage={setSnackMessage}
                                    />
                                </Paper>
                            </Grid>

                            <Grid item xs={12} lg={6}>
                                <Paper
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        marginTop: theme.spacing(6),
                                        minHeight: '240px',
                                    }}
                                >
                                    <Typography
                                        sx={{ textAlign: 'center', padding: 3 }}
                                        fontWeight="bold"
                                    >
                                        Your Marketplace Intro Video
                                    </Typography>
                                    <UploadVideo
                                        loading={videoUploading}
                                        button={false}
                                        deleteVideo={deleteVideo}
                                        uploadVideo={uploadVideo}
                                        playbackId={muxPlaybackId}
                                        setUploadVideo={setUploadVideo}
                                        onMessage={setSnackMessage}
                                    />
                                </Paper>
                            </Grid>
                            {/* Marketplace Enable Section */}
                            <Grid item xs={12}>
                                <Paper
                                    elevation={3}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        marginTop: theme.spacing(4),
                                        padding: theme.spacing(3),
                                        borderRadius: 2,
                                    }}
                                >
                                    <FormControl>
                                        <FormControlLabel
                                            checked={marketplaceEnabled}
                                            onChange={(event, checked) =>
                                                setMarketplaceEnabled(checked)
                                            }
                                            control={<Checkbox />}
                                            label={
                                                <Typography
                                                    variant="body1"
                                                    sx={{ fontWeight: 500 }}
                                                >
                                                    Enable my marketplace for this organization
                                                </Typography>
                                            }
                                            sx={{
                                                padding: theme.spacing(1),
                                            }}
                                        />
                                    </FormControl>
                                </Paper>
                            </Grid>
                            <Hidden smDown>
                                <SaveButton />
                            </Hidden>

                            <Hidden smUp>
                                <SaveButton />
                            </Hidden>
                        </React.Fragment>
                    )}
                </Grid>
            )}
            {tabValue === 1 && (
                <Container maxWidth={false} sx={{ width: '98%', mx: 'auto' }}>
                    <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                        <Grid item xs={2} sm={4} md={4}>
                            <Card sx={{ maxWidth: 345 }}>
                                <CardMedia
                                    sx={{
                                        p: 6,
                                        objectFit: 'contain',
                                    }}
                                    height={180}
                                    component="img"
                                    image={TemLogo}
                                    alt="1080 motion logo"
                                />
                                <CardContent>
                                    <Typography variant="body2" color="text.secondary">
                                        Connect 1080 Motion to analyze your data
                                    </Typography>
                                </CardContent>
                                <CardActions>
                                    {!integrations.find((i) => i.api_type === 'tem') ? (
                                        <Button
                                            size="small"
                                            onClick={() => {
                                                setTemModal(true);
                                            }}
                                        >
                                            Connect
                                        </Button>
                                    ) : (
                                        <Button disabled size="small">
                                            Connected
                                        </Button>
                                    )}
                                </CardActions>
                            </Card>
                        </Grid>
                    </Grid>

                    <Dialog open={temModal} onClose={() => setTemModal(false)}>
                        <Formik
                            onSubmit={(values) => {
                                if (!organization?.uuid) return;
                                setCreating(true);
                                createIntegration(organization.uuid, {
                                    api_key: values.apiKey,
                                    api_type: 'tem',
                                })
                                    .then((response) => {
                                        setTemModal(false);
                                        setIntegrations((i) => [...i, response.data]);
                                    })
                                    .catch(() => {})
                                    .finally(() => {
                                        setCreating(false);
                                    });
                            }}
                            validationSchema={yup.object({
                                apiKey: yup
                                    .string()
                                    .default('')
                                    .length(36, 'API key is invalid')
                                    .required('API Key is required'),
                            })}
                            initialValues={{ apiKey: '' }}
                        >
                            {({ handleChange, values, errors, touched }) => (
                                <Form>
                                    <DialogTitle>Add 1080 Motion API Key</DialogTitle>
                                    <DialogContent>
                                        <DialogContentText>
                                            Contact your 1080 Motion representative to get your API
                                            Key. Once you enter your API Key, you will be able to
                                            import your 1080 Motion data to your 1Tul account.{' '}
                                            {errors.apiKey}
                                        </DialogContentText>
                                        <TextField
                                            error={touched.apiKey && !!errors.apiKey}
                                            value={values.apiKey}
                                            name={'apiKey'}
                                            helperText={errors.apiKey as string}
                                            onChange={handleChange}
                                            autoFocus
                                            id="name"
                                            label="API Key"
                                            type="text"
                                            fullWidth
                                            variant="standard"
                                        />
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={() => setTemModal(false)}>Cancel</Button>
                                        <LoadingButton
                                            loadingIndicator="Saving..."
                                            variant="contained"
                                            loading={creating}
                                            disabled={!!errors.apiKey}
                                            type={'submit'}
                                        >
                                            Submit
                                        </LoadingButton>
                                    </DialogActions>
                                </Form>
                            )}
                        </Formik>
                    </Dialog>
                </Container>
            )}
            {tabValue === 2 && (
                <Container maxWidth={false} sx={{ width: '98%', mx: 'auto' }}>
                    <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                        {organization && (
                            <Grid item xs={2} sm={4} md={4}>
                                {!isSubscribedToOrgAccess(organization.uuid, subscriptions) ? (
                                    <Card sx={{ maxWidth: 345 }}>
                                        <CardMedia
                                            sx={{
                                                p: 6,
                                                objectFit: 'contain',
                                            }}
                                            height={180}
                                            component="img"
                                            image={SprintAnalysisLogo}
                                            alt="Sprint Analysis Logo"
                                        />
                                        <CardContent>
                                            <Typography variant="body2" color="text.secondary">
                                                Purchase Video AI - Sprint Analysis
                                            </Typography>
                                        </CardContent>
                                        {organization && (
                                            <CardActions>
                                                {!isSubscribedToVideoAnalysis(
                                                    organization.uuid,
                                                    subscriptions,
                                                ) ? (
                                                    <Button
                                                        disabled={checkingOut}
                                                        onClick={() => {
                                                            setCheckingOut(true);
                                                            createCheckoutSession(
                                                                window.location.href,
                                                                organization?.uuid,
                                                                process.env
                                                                    .REACT_APP_STRIPE_VIDEO_PRICE,
                                                            )
                                                                .then((response) => {
                                                                    window.location.href =
                                                                        response.data.url;
                                                                })
                                                                .catch(() => {
                                                                    setCheckingOut(false);
                                                                });
                                                        }}
                                                    >
                                                        {' '}
                                                        <CreditCardIcon sx={{ mr: 4 }} />
                                                        Purchase Video Analysis - Sprint
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        onClick={() => {
                                                            setCheckingOut(true);
                                                            createCheckoutSession(
                                                                window.location.href,
                                                                organization?.uuid,
                                                                process.env
                                                                    .REACT_APP_STRIPE_VIDEO_PRICE,
                                                            )
                                                                .then((response) => {
                                                                    window.location.href =
                                                                        response.data.url;
                                                                })
                                                                .catch(() => {
                                                                    setCheckingOut(false);
                                                                });
                                                        }}
                                                        size="small"
                                                    >
                                                        Manage Subscription
                                                    </Button>
                                                )}
                                            </CardActions>
                                        )}
                                    </Card>
                                ) : (
                                    <Card sx={{ maxWidth: 345 }}>
                                        <CardMedia
                                            sx={{
                                                p: 6,
                                                objectFit: 'contain',
                                            }}
                                            height={180}
                                            component="img"
                                            image={SprintAnalysisLogo}
                                            alt="Sprint Analysis Logo"
                                        />
                                        <CardContent>
                                            <Typography variant="body2" color="text.secondary">
                                                Video AI - Sprint Analysis
                                            </Typography>
                                        </CardContent>
                                        {organization && (
                                            <CardActions>
                                                <Button disabled={true}>
                                                    Included in Organization Subscription
                                                </Button>
                                            </CardActions>
                                        )}
                                    </Card>
                                )}
                            </Grid>
                        )}
                        <Grid item xs={2} sm={4} md={4}>
                            <Card sx={{ maxWidth: 345 }}>
                                <CardMedia
                                    sx={{
                                        p: 6,
                                        objectFit: 'contain',
                                    }}
                                    height={180}
                                    component="img"
                                    image={DbImage}
                                    alt="Sql Access"
                                />
                                <CardContent>
                                    <Typography variant="body2" color="text.secondary">
                                        Purchase SQL Access for all your BI tools
                                    </Typography>
                                </CardContent>
                                {organization && (
                                    <CardActions>
                                        {!isSubscribedToSqlAccess(
                                            organization.uuid,
                                            subscriptions,
                                        ) ? (
                                            <Button
                                                disabled={checkingOut}
                                                onClick={() => {
                                                    setCheckingOut(true);
                                                    createCheckoutSession(
                                                        window.location.href,
                                                        organization?.uuid,
                                                        process.env.REACT_APP_STRIPE_SQL_PRICE,
                                                    )
                                                        .then((response) => {
                                                            window.location.href =
                                                                response.data.url;
                                                        })
                                                        .catch(() => {
                                                            setCheckingOut(false);
                                                        });
                                                }}
                                            >
                                                {' '}
                                                <CreditCardIcon sx={{ mr: 4 }} />
                                                Purchase SQL Access
                                            </Button>
                                        ) : (
                                            <>
                                                <Button
                                                    onClick={() => {
                                                        setCheckingOut(true);
                                                        createCheckoutSession(
                                                            window.location.href,
                                                            organization?.uuid,
                                                            process.env.REACT_APP_STRIPE_SQL_PRICE,
                                                        )
                                                            .then((response) => {
                                                                window.location.href =
                                                                    response.data.url;
                                                            })
                                                            .catch(() => {
                                                                setCheckingOut(false);
                                                            });
                                                    }}
                                                    size="small"
                                                >
                                                    Manage Subscription
                                                </Button>
                                                <Button
                                                    onClick={() => {
                                                        setTabValue(3);
                                                    }}
                                                    size="small"
                                                >
                                                    SQL Users
                                                </Button>
                                            </>
                                        )}
                                    </CardActions>
                                )}
                            </Card>
                        </Grid>
                    </Grid>

                    <Dialog
                        open={sprintAnalysisModal}
                        onClose={() => setSprintAnalysisModal(false)}
                    >
                        <DialogTitle>Video AI - Sprint Analysis</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                Video must be shot in 60 frames per second.
                            </DialogContentText>
                        </DialogContent>
                    </Dialog>
                </Container>
            )}
            {tabValue === 3 && (
                <Container maxWidth={false} sx={{ width: '98%', mx: 'auto' }}>
                    {organization && <SqlUsersList organization={organization} />}
                </Container>
            )}
            {tabValue === 4 && (
                <Container maxWidth={false} sx={{ width: '98%', mx: 'auto' }}>
                    {organization && <MarketplaceSettingsPage organization={organization} />}
                </Container>
            )}
            {tabValue === 5 && (
                <Container maxWidth={false} sx={{ width: '98%', mx: 'auto' }}>
                    {organization && <PodsPage organization={organization} />}
                </Container>
            )}
            {tabValue === 6 && (
                <Container maxWidth={false} sx={{ width: '98%', mx: 'auto' }}>
                    {organization && <CircularProgress />}
                </Container>
            )}
            {tabValue === 7 && (
                <Container maxWidth={false} sx={{ width: '98%', mx: 'auto' }}>
                    {organization && <InvoicesPage organization={organization} />}
                </Container>
            )}
            {tabValue === 8 && (
                <Container maxWidth={false} sx={{ width: '98%', mx: 'auto' }}>
                    {organization && <AttributesPage organization={organization} />}
                </Container>
            )}
            {organization && sharingOpen && (
                <OrganizationSharingDrawer
                    onClose={toggleSharing}
                    open={sharingOpen}
                    organization={organization}
                    onUsersChanged={setSharedUsers}
                />
            )}

            <SnackBar
                open={!!notifyUserMessage}
                message={notifyUserMessage}
                setOpen={() => {
                    setNotifyUserMessage('');
                }}
                severity={success ? 'success' : 'error'}
            />
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                open={!!snackMessage}
                message={snackMessage ?? ''}
                autoHideDuration={5000}
                onClose={() => setSnackMessage(null)}
            />
        </TopBarWithShareIconLayout>
    );
}
